import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { CIRCLE_MAX_RADIUS_OPTIONS, CircleQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two circle dials (ADR 0106 §1).
 *
 * Largest radius bounds the radius every question is built from, so unlike the
 * volume panel's edge it applies to all four questions and is never hidden.
 * Both are basic tier, as the format's own panel is (ADR 0023 §2). Reads its
 * own slice of the settings (ADR 0081), so a change elsewhere on the panel
 * does not redraw it.
 */
export const CircleSettings = React.memo(function CircleSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { question, maxRadius, language } = useSetting((settings) => ({
        question: settings.CircleQuestion,
        maxRadius: settings.CircleMaxRadius,
        language: settings.Language,
    }));
    const t = useTranslation();

    function handleQuestionChange(value: CircleQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), CircleQuestion: value });
    }

    return (
        <div className="circleSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={question}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(CircleQuestionOptions), language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.largestRadiusLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.largestRadiusAria')}>
                {CIRCLE_MAX_RADIUS_OPTIONS.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`circleMaxRadius-${option}`}
                            value={option}
                            checked={maxRadius === option}
                            onChange={() => setSettings({ ...getSettings(), CircleMaxRadius: option })}
                        />
                        <label htmlFor={`circleMaxRadius-${option}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
});

export default CircleSettings;
